import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import LinkedText from '../common/LinkedText';

export interface Property {
  name: string;
  type: string;
  required: boolean;
  description: string;
  defaultValue?: string;
}

interface PropertiesTableSectionProps {
  properties?: Property[];
  title?: string;
}

const PropertiesTableSection: React.FC<PropertiesTableSectionProps> = ({
  properties,
  title = 'List of properties'
}) => {
  if (!properties || properties.length === 0) {
    return null;
  }

  const requiredProps = properties.filter(prop => prop.required);
  const optionalProps = properties.filter(prop => !prop.required);

  const renderPropertyRow = (property: Property) => (
    <TableRow 
      key={property.name}
      sx={{ 
        '&:hover': { 
          backgroundColor: '#f5f5f5' 
        }
      }}
    >
      <TableCell 
        sx={{ 
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: '0.9rem',
          color: '#1976d2',
          fontWeight: 500,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        {property.name}
      </TableCell>
      <TableCell 
        sx={{ 
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          fontSize: '0.875rem',
          color: '#666',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        {property.type}
      </TableCell>
      <TableCell 
        sx={{ 
          fontSize: '0.875rem',
          color: '#333',
          lineHeight: 1.6,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <LinkedText text={property.description} />
        {property.defaultValue && (
          <Box 
            component="span" 
            sx={{ 
              display: 'inline-block',
              mt: 0.5,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: '0.8rem',
              color: '#666',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '3px',
              ml: 1
            }}
          >
            {property.defaultValue}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ mb: 4, textAlign: 'left' }}>
      <Typography 
        variant="h5" 
        gutterBottom 
        sx={{ 
          fontWeight: 600, 
          mb: 3,
          color: '#333'
        }}
      >
        {title}
      </Typography>

      <TableContainer 
        component={Paper} 
        elevation={1}
        sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden'
        }}
      >
        <Table>
          {requiredProps.length > 0 && (
            <>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#d3d3d3' }}>
                  <TableCell 
                    colSpan={3} 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#333',
                      borderBottom: '2px solid #bdbdbd',
                      padding: '12px 16px'
                    }}
                  >
                    Properties
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      color: '#333',
                      borderBottom: '1px solid #e0e0e0',
                      width: '20%'
                    }}
                  >
                    Required
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      color: '#333',
                      borderBottom: '1px solid #e0e0e0',
                      width: '15%'
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      color: '#333',
                      borderBottom: '1px solid #e0e0e0',
                      width: '65%'
                    }}
                  >
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requiredProps.map(renderPropertyRow)}
              </TableBody>
            </>
          )}

          {optionalProps.length > 0 && (
            <>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      color: '#333',
                      borderBottom: '1px solid #e0e0e0',
                      borderTop: requiredProps.length > 0 ? '2px solid #bdbdbd' : 'none',
                      width: '20%'
                    }}
                  >
                    Optional
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      color: '#333',
                      borderBottom: '1px solid #e0e0e0',
                      borderTop: requiredProps.length > 0 ? '2px solid #bdbdbd' : 'none',
                      width: '15%'
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      color: '#333',
                      borderBottom: '1px solid #e0e0e0',
                      borderTop: requiredProps.length > 0 ? '2px solid #bdbdbd' : 'none',
                      width: '65%'
                    }}
                  >
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {optionalProps.map(renderPropertyRow)}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PropertiesTableSection;
